"use client";

import { useState, useEffect } from "react";
import { useRouter } from "next/navigation";
import { Button } from "@/components/themes/ui/button";
import { Separator } from "@/components/themes/ui/separator";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/themes/ui/collapsible";
import { ChevronUp, ChevronDown } from "lucide-react";
import CouponSection from "@/components/themes/coupon-section";
import { useUser } from "@/providers/userContext";
import { getCurrencySymbol } from "@/services/storeService";

interface PriceSummarySectionProps {
  setCartInfoData: (data: any) => void;
}

export default function PriceSummarySection({
  setCartInfoData,
}: PriceSummarySectionProps) {
  const { cartinfoData } = useUser();
  const router = useRouter();
  const [isPriceDetailsOpen, setIsPriceDetailsOpen] = useState(true);
  const [cartcouponlist, setCartCouponList] = useState<any[]>([]);

  // Use cartinfoData values if available, otherwise calculate
  const subtotal =
    cartinfoData?.subtotal && typeof cartinfoData.subtotal === "string"
      ? parseFloat(cartinfoData.subtotal)
      : cartinfoData?.products?.reduce(
        (sum: number, item: any) =>
          sum + (parseFloat(item.sp) || 0) * (parseInt(item.cartquantity) || 1),
        0
      ) || 0;

  const totalMRP =
    cartinfoData?.mrptotal && typeof cartinfoData.mrptotal === "string"
      ? parseFloat(cartinfoData.mrptotal)
      : cartinfoData?.products?.reduce(
        (sum: number, item: any) =>
          sum + (parseFloat(item.mrp) || 0) * (parseInt(item.cartquantity) || 1),
        0
      ) || 0;

  // Calculate delivery fee (align with cartCalculation)
  const deliveryFee =
    cartinfoData?.products?.length === 0 || subtotal >= 299 ? 0 : 45;

  // Calculate discount based on coupons
  const getCartDiscount = () => {
    let totalDiscount = 0;
    const amount = subtotal;

    if (cartcouponlist.length > 0) {
      cartcouponlist.forEach((_coupon) => {
        if (_coupon.discounttype === "percentage") {
          totalDiscount += (amount * _coupon.discount) / 100;
        } else if (_coupon.discounttype === "amount") {
          totalDiscount += _coupon.discount;
        }
      });
    }

    return totalDiscount;
  };

  // Calculate total amount
  const getCartTotal = () => {
    const totalDiscount = getCartDiscount();
    const total = subtotal + deliveryFee - totalDiscount;
    return total < 0 ? 0 : total;
  };

  // Get discount and total amounts
  const discountAmount = getCartDiscount();
  const totalAmount = getCartTotal();

  const movetocheckout = () => {
    router.push("/checkout");
  };

  useEffect(() => {
    // recalc everything every time the cart array changes
    const subtotal = cartinfoData?.products?.reduce(
      (s, p) => s + parseFloat(p.sp || 0) * (p.cartquantity || 1),
      0
    ) ?? 0;

    const mrpTotal = cartinfoData?.products?.reduce(
      (s, p) => s + parseFloat(p.mrp || 0) * (p.cartquantity || 1),
      0
    ) ?? 0;

    const delivery = cartinfoData?.products?.length === 0 || subtotal >= 299 ? 0 : 45;

    const discount = cartcouponlist.reduce((d, c) => {
      if (c.discounttype === "percentage") return d + (subtotal * c.discount) / 100;
      return d + c.discount;
    }, 0);

    const total = subtotal + delivery - discount;

    // push the fresh numbers into context (so CartView also sees them)
    setCartInfoData({
      ...cartinfoData,
      subtotal: subtotal.toFixed(2),
      mrptotal: mrpTotal.toFixed(2),
      shipping: delivery.toFixed(2),
      ordertotal: total < 0 ? "0.00" : total.toFixed(2),
      total: total < 0 ? "0.00" : total.toFixed(2),
    });
  }, [cartinfoData?.products, cartcouponlist, setCartInfoData]);

  return (
    <div className="md:w-5/12 w-full p-4 space-y-4">
      <CouponSection
        cartcouponlist={cartcouponlist}
        setCartCouponList={setCartCouponList}
        displaycartcouponlist={cartcouponlist}
        totalamount={subtotal}
      />
      <Collapsible
        open={isPriceDetailsOpen}
        onOpenChange={setIsPriceDetailsOpen}
        className="border-solid border-gray-300 border-[1px] space-y-[16px] h-fit rounded-[8px] p-4 rounded-border_bg"
      >
        <CollapsibleTrigger asChild>
          <div className="flex justify-between items-center cursor-pointer">
            <h2 className="text-lg">Price Details</h2>
            <Button variant="ghost" size="icon" className="h-auto w-auto p-0">
              {isPriceDetailsOpen ? (
                <ChevronUp className="h-5 w-5 transition-transform duration-300" />
              ) : (
                <ChevronDown className="h-5 w-5 transition-transform duration-300" />
              )}
              <span className="sr-only">Toggle price details</span>
            </Button>
          </div>
        </CollapsibleTrigger>
        <CollapsibleContent className="overflow-hidden h-fit transition-all duration-300 data-[state=closed]:animate-collapsible-up data-[state=open]:animate-collapsible-down">
          <div className="grid gap-2 text-sm">
            <div className="flex justify-between">
              <span>Total Selling Price</span>
              <span>{getCurrencySymbol()} {subtotal.toFixed(2)}</span>
            </div>
            <div className="flex justify-between">
              <span>Discount Amount</span>
              <span className="text-green-600">
                - {getCurrencySymbol()} {discountAmount.toFixed(2)}
              </span>
            </div>
            <div className="flex justify-between">
              <span>Delivery Fee</span>
              {deliveryFee === 0 ? (
                <span className="text-green-600">To be Estimate</span>
              ) : (
                <span>{getCurrencySymbol()} {deliveryFee.toFixed(2)}</span>
              )}
            </div>
            <hr className="mt-4 h-[1.5px] border-0 bg-[repeating-linear-gradient(to_right,#cbcbcb_0_12px,transparent_10px_20px)]" />
            <Separator className="" />
            <div className="flex justify-between font-bold text-base">
              <span>Subtotal</span>
              <span>{getCurrencySymbol()} {totalAmount.toFixed(2)}</span>
            </div>
          </div>
        </CollapsibleContent>
      </Collapsible>
      <div className="bg-white z-10 md:!relative !sticky !top-[0px] !bottom-[0px] !py-[10px]">
        <div className="flex mb-3 justify-between items-center text-xl font-bold">
          <span>Total Amount</span>
          <span>{getCurrencySymbol()} {totalAmount.toFixed(2)}</span>
        </div>
        <Button
          size="lg"
          className="w-full bg-primary border border-primary hover:bg-primary_hover text-white rounded-[8px]"
          onClick={movetocheckout}
          disabled={cartinfoData?.products?.length === 0}
        >
          Go To Checkout
        </Button>
      </div>
    </div>
  );
}